import * as React from 'react';
import { connect } from 'react-redux';
import { cloneDeep } from 'lodash';

import { Form } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import CampaignPayoutsInstallmentsModalFields from './fields/CampaignPayoutsInstallmentsModalFields';
import CampaignPayoutsInstallmentsModalUnallocated from './unallocated/CampaignPayoutsInstallmentsModalUnallocated';
import PromptExit from 'libs/router/prompt-exit/PromptExit';
import { ModalFrame, ModalFrameEntity, ModalFrameFooter, ModalFrameInner } from 'components/modal';

import {
  createPayoutsBySubcampaign,
  fetchPayoutsBySubcampaign,
  resetPayoutsBySubcampaign,
  updatePayoutsBySubcampaign
} from 'modules/subcampaigns/reducers/subcampaignPayouts';

import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';
import { getPayoutDropdownDates } from 'utils/payouts';
import { convertDateToISOFormat } from 'utils/date';

import './campaign-payouts-installments-modal.scss';

import type {
  Payout,
  PayoutType,
  SubcampaignInfluencerProfile
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./campaign-payouts-installments-modal.i18n.json'));

const FIELDS = {
  note: '',
  amount: 0,
  scheduledDate: '',
  releaseDate: '',
  payoutType: 'offsite' as PayoutType
};

type FieldType = HTMLInputElement | HTMLSelectElement;

type OwnProps = {
  influencerProfile: SubcampaignInfluencerProfile | undefined | null,
  campaignId: number,
  subcampaignId: number,
  totalBudget: number,
  campaignEndDatetime: string,
  onCloseModal: () => void
};

type StateProps = {
  payoutsBySubcampaign: Array<Payout>,
  isLoading: boolean
};

type DispatchProps = {
  dispatchFetchPayoutsBySubcampaign: (a: number) => void,
  dispatchCreatePayoutsBySubcampaign: (c: Array<Payout>, b: number, a: number) => void,
  dispatchUpdatePayoutsBySubcampaign: (c: Array<Payout>, b: number, a: number) => void,
  dispatchResetPayoutsBySubcampaign: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  payouts: Array<Payout>,
  budget: number,
  isModified: boolean
};

class CampaignPayoutsInstallmentsModal extends React.PureComponent<Props, State> {
  static getUpdatedBudget(totalBudget: number, payouts: Array<Payout>): number {
    return payouts.reduce((budget, payoutObject) => (
      budget - payoutObject.amount
    ), totalBudget);
  }

  state = {
    payouts: [{ ...FIELDS }],
    budget: this.props.totalBudget,
    isModified: false
  };

  componentDidMount() {
    const { subcampaignId, dispatchFetchPayoutsBySubcampaign } = this.props;

    dispatchFetchPayoutsBySubcampaign(subcampaignId);
  }

  componentDidUpdate(prevProps: Props) {
    const { payoutsBySubcampaign, totalBudget } = this.props;

    if (payoutsBySubcampaign !== prevProps.payoutsBySubcampaign) {
      const payouts = [...(payoutsBySubcampaign || [])];

      // automatically add blank fields only when in "create" mode
      if (payouts.length === 0) {
        payouts.push({ ...FIELDS });
      }

      this.setState({ // eslint-disable-line react/no-did-update-set-state
        payouts,
        budget: CampaignPayoutsInstallmentsModal.getUpdatedBudget(totalBudget, payouts),
        isModified: false
      });
    }
  }

  componentWillUnmount() {
    const { dispatchResetPayoutsBySubcampaign } = this.props;

    dispatchResetPayoutsBySubcampaign();
  }

  handleAddField = (): void => {
    const { payouts } = this.state;

    this.setState({
      payouts: [
        ...payouts,
        { ...FIELDS }
      ],
      isModified: true
    });
  };

  handleRemoveField = (position: number): void => {
    const { totalBudget } = this.props;
    const { payouts } = this.state;
    const newPayouts = [...payouts];

    newPayouts.splice(position, 1);

    this.setState({
      payouts: newPayouts,
      budget: CampaignPayoutsInstallmentsModal.getUpdatedBudget(totalBudget, newPayouts),
      isModified: true
    });
  };

  handleFieldChange = (event: SyntheticEvent<FieldType>): void => {
    const { totalBudget } = this.props;
    const { payouts, budget } = this.state;
    const target = event.currentTarget;
    const name = target.name || '';
    const value = target.value || '';
    const position = Number(target.dataset.position) || 0;
    const newPayouts = cloneDeep(payouts);

    if (name === 'note') {
      newPayouts[position].note = value;
    }

    if (name === 'amount') {
      newPayouts[position].amount = Number(value);
    }

    this.setState({
      payouts: newPayouts,
      budget: name === 'amount'
        ? CampaignPayoutsInstallmentsModal.getUpdatedBudget(totalBudget, newPayouts)
        : budget,
      isModified: true
    });
  };

  handleDateChange = (
    value: string,
    position: number
  ): void => {
    const { payouts, budget } = this.state;
    const newPayouts = cloneDeep(payouts);

    newPayouts[position].scheduledDate = convertDateToISOFormat(value);

    this.setState({
      payouts: newPayouts,
      budget,
      isModified: true
    });
  };

  handleCreate = (): void => {
    const { campaignId, subcampaignId, onCloseModal, dispatchCreatePayoutsBySubcampaign } = this.props;
    const { payouts } = this.state;

    dispatchCreatePayoutsBySubcampaign(payouts, campaignId, subcampaignId);
    onCloseModal();
  };

  handleEdit = (): void => {
    const { campaignId, subcampaignId, onCloseModal, dispatchUpdatePayoutsBySubcampaign } = this.props;
    const { payouts } = this.state;
    const newPayouts = payouts.filter(payout => !payout.releaseDate);

    dispatchUpdatePayoutsBySubcampaign(newPayouts, campaignId, subcampaignId);
    onCloseModal();
  };

  render(): React.ReactNode {
    const {
      payoutsBySubcampaign,
      influencerProfile,
      totalBudget,
      campaignEndDatetime,
      isLoading,
      onCloseModal
    } = this.props;

    const { payouts, budget, isModified } = this.state;
    const fieldKey = influencerProfile && (influencerProfile.artistName || influencerProfile.name) || '';
    const overBudget = budget < 0;
    const modalFrameSubtitle = __t(
      'campaign_payouts_installments_modal.labels.total_budget',
      { budget: renderPrice(totalBudget) }
    );

    return (
      <ModalFrame
        title={__t('campaign_payouts_installments_modal.labels.payout_installments')}
        subtitle={modalFrameSubtitle}
      >
        <Form
          onSubmit={payoutsBySubcampaign.length ? this.handleEdit : this.handleCreate}
        >
          {
            isModified &&
              <PromptExit type="modal" />
          }
          <ModalFrameEntity
            influencerProfile={influencerProfile}
          />
          <ModalFrameInner>
            <section className="campaign-payouts-installments-modal">
              <CampaignPayoutsInstallmentsModalFields
                influencerProfile={influencerProfile}
                fieldKey={fieldKey}
                payouts={payouts}
                dropdownDates={getPayoutDropdownDates(campaignEndDatetime)}
                isLoading={isLoading}
                isEditMode={payoutsBySubcampaign && payoutsBySubcampaign.length > 0}
                onFieldChange={this.handleFieldChange}
                onDateChange={this.handleDateChange}
                onAddField={this.handleAddField}
                onRemoveField={this.handleRemoveField}
              />
              {
                overBudget && (
                  <div className="campaign-payments-installments-modal__budget-exceeded">
                    <span>
                      {__t('campaign_payouts_installments_modal.labels.over_budget')}
                    </span>
                  </div>
                )
              }
              <CampaignPayoutsInstallmentsModalUnallocated
                budget={budget}
                campaignEndDatetime={campaignEndDatetime}
              />
            </section>
          </ModalFrameInner>
          <ModalFrameFooter>
            <ActionButton
              color="tertiary"
              onClick={onCloseModal}
            >
              {__t('collabary_common.action_button.cancel')}
            </ActionButton>
            <ActionButton
              type="submit"
              disabled={overBudget}
            >
              {__t('collabary_common.action_button.save')}
            </ActionButton>
          </ModalFrameFooter>
        </Form>
      </ModalFrame>
    );
  }
}

const mapStateToProps = ({ subcampaignPayouts }): StateProps => ({
  payoutsBySubcampaign: subcampaignPayouts.payouts,
  isLoading: subcampaignPayouts.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchPayoutsBySubcampaign: (subcampaignId: number): void => {
    dispatch(fetchPayoutsBySubcampaign(subcampaignId));
  },

  dispatchCreatePayoutsBySubcampaign: (payouts: Array<Payout>, campaignId: number, subcampaignId: number): void => {
    dispatch(createPayoutsBySubcampaign(payouts, campaignId, subcampaignId));
  },

  dispatchUpdatePayoutsBySubcampaign: (payouts: Array<Payout>, campaignId: number, subcampaignId: number): void => {
    dispatch(updatePayoutsBySubcampaign(payouts, campaignId, subcampaignId));
  },

  dispatchResetPayoutsBySubcampaign: (): void => {
    dispatch(resetPayoutsBySubcampaign());
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignPayoutsInstallmentsModal);
